import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import PageHero from '../components/Common/PageHero';
import { MigrationHistoryDate, MigrationHistoryFile, migrationsAPI } from '../services/api';

const formatHistoryTime = (value: string) => new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
}).format(new Date(value));

const isDateKeyInRange = (dateKey: string, fromDate: string, toDate: string) => {
  if (fromDate && dateKey < fromDate) return false;
  if (toDate && dateKey > toDate) return false;
  return true;
};

const MigrationHistory: React.FC = () => {
  const navigate = useNavigate();
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyDates, setHistoryDates] = useState<MigrationHistoryDate[]>([]);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const filteredHistoryDates = useMemo(
    () => historyDates.filter((date) => isDateKeyInRange(date.dateKey, fromDate, toDate)),
    [fromDate, historyDates, toDate]
  );

  const visibleFileCount = useMemo(
    () => filteredHistoryDates.reduce((sum, date) => sum + date.totalFiles, 0),
    [filteredHistoryDates]
  );

  const saveBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const loadHistory = async () => {
    try {
      setIsLoadingHistory(true);
      const response = await migrationsAPI.getHistory();
      setHistoryDates(response.dates);
      setExpandedDates((current) => {
        if (current.size > 0 || response.dates.length === 0) return current;
        return new Set([response.dates[0].dateKey]);
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to load migration history');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    void loadHistory();
  }, []);

  const toggleDate = (dateKey: string) => {
    setExpandedDates((current) => {
      const next = new Set(current);
      if (next.has(dateKey)) {
        next.delete(dateKey);
      } else {
        next.add(dateKey);
      }
      return next;
    });
  };

  const downloadHistoryFile = async (file: MigrationHistoryFile) => {
    try {
      const blob = await migrationsAPI.downloadHistoryFile(file.dateKey, file.filename);
      saveBlob(blob, file.filename);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to download migration file');
    }
  };

  const downloadHistoryDate = async (date: MigrationHistoryDate) => {
    try {
      const blob = await migrationsAPI.downloadHistoryDateZip(date.dateKey);
      saveBlob(blob, `Migration History - ${date.dateKey}.zip`);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to download migration ZIP');
    }
  };

  return (
    <div className="space-y-8">
      <PageHero
        badge={{ label: 'Migration archive', tone: 'cyan' }}
        title="Migration History"
        description="Review previous migration runs by date, stage, and account. Download one Excel file or all files for a date."
        stats={[
          { icon: 'fas fa-calendar-days', label: `${filteredHistoryDates.length} date${filteredHistoryDates.length === 1 ? '' : 's'}` },
          { icon: 'fas fa-file-excel', label: `${visibleFileCount} file${visibleFileCount === 1 ? '' : 's'}` },
        ]}
        actions={
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/migrations')}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-5 py-3 text-sm font-black text-white shadow-lg transition hover:bg-white/15"
            >
              <i className="fas fa-arrow-left"></i>
              Back to Migrations
            </button>
            <button
              onClick={loadHistory}
              disabled={isLoadingHistory}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white px-5 py-3 text-sm font-black text-slate-900 shadow-lg transition hover:bg-cyan-50 disabled:cursor-not-allowed disabled:bg-gray-200"
            >
              <i className={`fas ${isLoadingHistory ? 'fa-spinner animate-spin' : 'fa-rotate'}`}></i>
              Refresh
            </button>
          </div>
        }
      />

      <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-lg font-black text-gray-900">Filter by date</h2>
            <p className="mt-1 text-sm font-semibold text-gray-500">Choose a date range to show only matching migration history.</p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <label className="space-y-1">
              <span className="text-xs font-black uppercase tracking-wide text-gray-500">From</span>
              <input
                type="date"
                value={fromDate}
                max={toDate || undefined}
                onChange={(event) => setFromDate(event.target.value)}
                className="w-full rounded-2xl border border-gray-200 px-4 py-2.5 text-sm font-bold text-gray-800 outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-black uppercase tracking-wide text-gray-500">To</span>
              <input
                type="date"
                value={toDate}
                min={fromDate || undefined}
                onChange={(event) => setToDate(event.target.value)}
                className="w-full rounded-2xl border border-gray-200 px-4 py-2.5 text-sm font-bold text-gray-800 outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
              />
            </label>
            <button
              onClick={() => {
                setFromDate('');
                setToDate('');
              }}
              disabled={!fromDate && !toDate}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-gray-200 px-4 py-2.5 text-sm font-black text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
            >
              <i className="fas fa-filter-circle-xmark"></i>
              Clear
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {isLoadingHistory && historyDates.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-gray-200 bg-white p-8 text-center text-sm font-semibold text-gray-500">Loading migration history...</div>
        ) : historyDates.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-gray-200 bg-white p-8 text-center text-sm font-semibold text-gray-500">No migration history files have been generated yet.</div>
        ) : filteredHistoryDates.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-gray-200 bg-white p-8 text-center text-sm font-semibold text-gray-500">No migration history matches the selected date range.</div>
        ) : filteredHistoryDates.map((date) => {
          const isExpanded = expandedDates.has(date.dateKey);
          const files = date.stages.flatMap((stage) => stage.files.map((file) => ({ ...file, stageLabel: stage.stageLabel })));
          return (
            <div key={date.dateKey} className="overflow-hidden rounded-3xl border border-gray-100 bg-gray-50 shadow-sm">
              <button
                onClick={() => toggleDate(date.dateKey)}
                className="flex w-full flex-col gap-3 bg-white p-5 text-left transition hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <div className="text-lg font-black text-gray-900">{date.dateLabel}</div>
                  <div className="mt-1 text-sm font-semibold text-gray-500">{date.totalFiles} Excel file{date.totalFiles === 1 ? '' : 's'} generated</div>
                </div>
                <div className="inline-flex items-center gap-3 text-sm font-bold text-blue-700">
                  {isExpanded ? 'Hide files' : 'Show files'}
                  <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                </div>
              </button>

              {isExpanded && (
                <div className="space-y-4 p-5">
                  <div className="flex justify-end">
                    <button
                      onClick={() => downloadHistoryDate(date)}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-black text-white shadow-sm transition hover:bg-emerald-700"
                    >
                      <i className="fas fa-file-zipper"></i>
                      Download All
                    </button>
                  </div>

                  <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-100 text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-black uppercase tracking-wide text-gray-500">Name</th>
                            <th className="px-4 py-3 text-left text-xs font-black uppercase tracking-wide text-gray-500">Stage</th>
                            <th className="px-4 py-3 text-left text-xs font-black uppercase tracking-wide text-gray-500">Account</th>
                            <th className="px-4 py-3 text-left text-xs font-black uppercase tracking-wide text-gray-500">Time</th>
                            <th className="px-4 py-3 text-left text-xs font-black uppercase tracking-wide text-gray-500">Results</th>
                            <th className="px-4 py-3 text-right text-xs font-black uppercase tracking-wide text-gray-500">Download</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {files.map((file) => (
                            <tr key={`${file.dateKey}-${file.filename}`} className="transition hover:bg-slate-50">
                              <td className="max-w-xs px-4 py-3 font-bold text-gray-900">
                                <div className="truncate">{file.filename}</div>
                              </td>
                              <td className="px-4 py-3 font-semibold text-gray-700">{file.stageLabel}</td>
                              <td className="px-4 py-3 font-semibold text-gray-700">{file.accountLabel}</td>
                              <td className="px-4 py-3 text-gray-600">{formatHistoryTime(file.createdAt)}</td>
                              <td className="px-4 py-3">
                                <div className="flex flex-wrap gap-2">
                                  <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-700">{file.totalRows} triggered</span>
                                  <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700">{file.successfulRows} successful</span>
                                  <span className="rounded-full bg-rose-100 px-2.5 py-1 text-xs font-bold text-rose-700">{file.failedRows} failed</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  onClick={() => downloadHistoryFile(file)}
                                  title={`Download ${file.filename}`}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-white transition hover:bg-slate-800"
                                >
                                  <i className="fas fa-download"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={() => downloadHistoryDate(date)}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-200 px-4 py-2 text-xs font-black text-emerald-700 transition hover:bg-emerald-50"
                    >
                      <i className="fas fa-file-zipper"></i>
                      Download All
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MigrationHistory;
